import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useUserSettings } from '../contexts/UserSettingsContext';
import FeatureGuard from '../components/FeatureGuard';

// Symbol options for the dropdown
const symbolOptions = [
  { value: 'OANDA:XAUUSD', label: 'Gold (XAU/USD)' },
  { value: 'BITSTAMP:BTCUSD', label: 'Bitcoin (BTC/USD)' },
  { value: 'BITSTAMP:ETHUSD', label: 'Ethereum (ETH/USD)' },
  { value: 'FX_IDC:EURUSD', label: 'EUR/USD' },
  { value: 'FOREXCOM:SPXUSD', label: 'S&P 500 Index' },
  { value: 'FOREXCOM:NSXUSD', label: 'NASDAQ 100' },
  { value: 'OANDA:XAGUSD', label: 'Silver (XAG/USD)' },
  { value: 'OANDA:USDJPY', label: 'USD/JPY' },
  { value: 'OANDA:GBPUSD', label: 'GBP/USD' },
];

// Symbol Selector Component
const SymbolSelector: React.FC<{ selectedSymbol: string; onSymbolChange: (symbol: string) => void }> = ({
  selectedSymbol,
  onSymbolChange,
}) => {
  const handleChange = (event: SelectChangeEvent) => {
    onSymbolChange(event.target.value);
  };

  return (
    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
      <FormControl sx={{ minWidth: 300 }}>
        <InputLabel id="symbol-select-label" sx={{ color: 'text.secondary' }}>
          Select Symbol
        </InputLabel>
        <Select
          labelId="symbol-select-label"
          value={selectedSymbol}
          label="Select Symbol"
          onChange={handleChange}
          sx={{
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.23)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.4)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main',
            },
            '& .MuiSelect-select': {
              color: 'text.primary',
            },
            '& .MuiSvgIcon-root': {
              color: 'text.secondary',
            },
          }}
        >
          {symbolOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

// TradingView Ticker Tape Component
const TradingViewTickerTape: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { settings } = useUserSettings();

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !settings.tickerSymbols.length) return;

    // Clear any existing content
    container.innerHTML = `
      <div class="tradingview-widget-container__widget"></div>
    `;

    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-ticker-tape.js';
    script.type = 'text/javascript';
    script.async = true;
    script.onload = () => {
      // Script loaded successfully
    };
    script.onerror = () => {
      console.warn('Failed to load TradingView ticker tape widget');
    };
    
    // Convert settings symbols to ticker format
    const tickerSymbols = settings.tickerSymbols.map(symbol => ({
      proName: symbol.proName,
      title: symbol.title || symbol.description || symbol.proName.split(':')[1] || symbol.proName
    }));
    
    script.innerHTML = JSON.stringify({
      symbols: tickerSymbols,
      showSymbolLogo: true,
      isTransparent: false,
      largeChartUrl: "",
      displayMode: "adaptive",
      colorTheme: "dark",
      locale: "en"
    });

    const widgetContainer = container.querySelector('.tradingview-widget-container__widget');
    if (widgetContainer) {
      widgetContainer.appendChild(script);
    }

    return () => {
      // Cleanup on unmount
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [settings.tickerSymbols]);

  return (
    <div className="tradingview-widget-container" ref={containerRef}>
      <div className="tradingview-widget-container__widget"></div>
    </div>
  );
};

// TradingView Symbol Info Component
const TradingViewSymbolInfo: React.FC<{ symbol: string }> = ({ symbol }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Add a small delay to prevent conflicts with ticker tape
    const timer = setTimeout(() => {
      // Clear any existing content
      container.innerHTML = `
        <div class="tradingview-widget-container__widget"></div>
        <div class="tradingview-widget-copyright">
          
        </div>
      `;

      const script = document.createElement('script');
      script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-symbol-info.js';
      script.type = 'text/javascript';
      script.async = true;
      script.onload = () => {
        // Script loaded successfully
      };
      script.onerror = () => {
        console.warn('Failed to load TradingView symbol info widget');
      };
      
      script.innerHTML = JSON.stringify({
        symbol: symbol,
        width: "100%",
        locale: "en",
        colorTheme: "dark",
        isTransparent: false
      });

      const widgetContainer = container.querySelector('.tradingview-widget-container__widget');
      if (widgetContainer) {
        widgetContainer.appendChild(script);
      }
    }, 100);

    return () => {
      // Cleanup timer and container
      clearTimeout(timer);
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [symbol]);

  return (
    <div className="tradingview-widget-container" ref={containerRef}>
      <div className="tradingview-widget-container__widget"></div>
      <div className="tradingview-widget-copyright">
        
      </div>
    </div>
  );
};

// TradingView Advanced Chart Component
const TradingViewAdvancedChart: React.FC<{ symbol: string }> = ({ symbol }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Add a delay to prevent conflicts with other widgets
    const timer = setTimeout(() => {
      // Clear any existing content
      container.innerHTML = `
        <div class="tradingview-widget-container__widget" style="height: calc(100% - 32px); width: 100%;"></div>
        <div class="tradingview-widget-copyright">
          
        </div>
      `;

      const script = document.createElement('script');
      script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
      script.type = 'text/javascript';
      script.async = true;
      script.onload = () => {
        // Script loaded successfully
      };
      script.onerror = () => {
        console.warn('Failed to load TradingView advanced chart widget');
      };
      
      script.innerHTML = JSON.stringify({
        autosize: true,
        symbol: symbol,
        interval: "D",
        timezone: "Africa/Johannesburg",
        theme: "dark",
        style: "1",
        locale: "en",
        allow_symbol_change: false,
        support_host: "https://www.tradingview.com"
      });

      const widgetContainer = container.querySelector('.tradingview-widget-container__widget');
      if (widgetContainer) {
        widgetContainer.appendChild(script);
      }
    }, 200);

    return () => {
      // Cleanup timer and container
      clearTimeout(timer);
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [symbol]);

  return (
    <div 
      className="tradingview-widget-container" 
      ref={containerRef}
      style={{ height: "600px", width: "100%" }}
    >
      <div 
        className="tradingview-widget-container__widget" 
        style={{ height: "calc(100% - 32px)", width: "100%" }}
      ></div>
      <div className="tradingview-widget-copyright">
        
      </div>
    </div>
  );
};

// TradingView Technical Analysis Component
const TradingViewTechnicalAnalysis: React.FC<{ symbol: string }> = ({ symbol }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Add a delay to prevent conflicts with other widgets
    const timer = setTimeout(() => {
      // Clear any existing content
      container.innerHTML = `
        <div class="tradingview-widget-container__widget" style="height: 100%; width: 100%;"></div>
        <div class="tradingview-widget-copyright">
        
        </div>
      `;

      const script = document.createElement('script');
      script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-technical-analysis.js';
      script.type = 'text/javascript';
      script.async = true;
      script.onload = () => {
        // Script loaded successfully
      };
      script.onerror = () => {
        console.warn('Failed to load TradingView technical analysis widget');
      };
      
      script.innerHTML = JSON.stringify({
        interval: "15m",
        width: "100%",
        isTransparent: false,
        height: "100%",
        symbol: symbol,
        showIntervalTabs: true,
        displayMode: "multiple",
        locale: "en",
        colorTheme: "dark"
      });

      const widgetContainer = container.querySelector('.tradingview-widget-container__widget');
      if (widgetContainer) {
        widgetContainer.appendChild(script);
      }
    }, 250);

    return () => {
      // Cleanup timer and container
      clearTimeout(timer);
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [symbol]);

  return (
    <div 
      className="tradingview-widget-container" 
      ref={containerRef}
      style={{ height: "600px", width: "100%", minHeight: "500px" }}
    >
      <div className="tradingview-widget-container__widget" style={{ height: "100%", width: "100%" }}></div>
      <div className="tradingview-widget-copyright">
      
      </div>
    </div>
  );
};

// TradingView News Component
const TradingViewNews: React.FC<{ symbol: string }> = ({ symbol }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Add a delay to prevent conflicts with other widgets
    const timer = setTimeout(() => {
      // Clear any existing content
      container.innerHTML = `
        <div class="tradingview-widget-container__widget"></div>
        <div class="tradingview-widget-copyright">
        
        </div>
      `;

      const script = document.createElement('script');
      script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-timeline.js';
      script.type = 'text/javascript';
      script.async = true;
      script.onload = () => {
        // Script loaded successfully
      };
      script.onerror = () => {
        console.warn('Failed to load TradingView news widget');
      };
      
      script.innerHTML = JSON.stringify({
        feedMode: "symbol",
        symbol: symbol,
        isTransparent: false,
        displayMode: "regular",
        width: "100%",
        height: "100%",
        colorTheme: "dark",
        locale: "en"
      });

      const widgetContainer = container.querySelector('.tradingview-widget-container__widget');
      if (widgetContainer) {
        widgetContainer.appendChild(script);
      }
    }, 300);

    return () => {
      // Cleanup timer and container
      clearTimeout(timer);
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [symbol]);

  return (
    <div 
      className="tradingview-widget-container" 
      ref={containerRef}
      style={{ height: "100%", width: "100%" }}
    >
      <div className="tradingview-widget-container__widget"></div>
      <div className="tradingview-widget-copyright">
      
      </div>
    </div>
  );
};

const MarketIntelligencePage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedSymbol, setSelectedSymbol] = useState<string>('OANDA:XAUUSD');

  const handleSymbolChange = (symbol: string) => {
    setSelectedSymbol(symbol);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    },
  };

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
      <Container maxWidth="lg">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Page Header */}
          <motion.div variants={itemVariants}>
            <Typography
              variant={isMobile ? 'h4' : 'h2'}
              component="h1"
              gutterBottom
              sx={{ mb: 1 }}
            >
              📈 Market Intelligence
            </Typography>
            
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 4 }}
            >
              Real-time market data powered by TradingView
            </Typography>
          </motion.div>

          {/* TradingView Ticker Tape */}
          <motion.div variants={itemVariants} style={{ marginBottom: '2rem' }}>
            <TradingViewTickerTape />
          </motion.div>

          {/* Symbol Selector */}
          <motion.div variants={itemVariants}>
            <SymbolSelector 
              selectedSymbol={selectedSymbol} 
              onSymbolChange={handleSymbolChange} 
            />
          </motion.div>

          {/* TradingView Symbol Info */}
          <motion.div variants={itemVariants} style={{ marginBottom: '2rem' }}>
            <TradingViewSymbolInfo symbol={selectedSymbol} />
          </motion.div>

          {/* TradingView Advanced Chart */}
          <motion.div variants={itemVariants} style={{ marginBottom: '2rem' }}>
            <TradingViewAdvancedChart symbol={selectedSymbol} />
          </motion.div>

          {/* TradingView Technical Analysis */}
          <motion.div variants={itemVariants} style={{ marginBottom: '2rem' }}>
            <TradingViewTechnicalAnalysis symbol={selectedSymbol} />
          </motion.div>

          {/* TradingView News */}
          <motion.div variants={itemVariants} style={{ marginBottom: '2rem' }}>
            <TradingViewNews symbol={selectedSymbol} />
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

function MarketIntelligenceFeature() {
  return (
    <FeatureGuard featureId="market_intelligence">
      <MarketIntelligencePage />
    </FeatureGuard>
  );
}

export const MarketIntelligence = MarketIntelligenceFeature;

export default MarketIntelligenceFeature; 